import { useState, useEffect, useRef } from "react";
import { Textarea } from "@/components/ui/textarea.tsx";
import stop from "@/assets/search/stop.webp";
import enter from "@/assets/search/enter.webp";
import enterRead from "@/assets/search/entetRead.png";
import "./index.css";
import { useTranslation } from "@/locales";
import modelService from "@/services/modelService";
import { envStorage } from "@/utils/envCache";
import useCounterStore from "@/store";
import { cn } from "@/lib/utils";
import ThinkingIcon from "@/assets/think.svg?react";
interface Props {
  onSearch: (query: string, modelCode?: string) => Promise<void>;
  onStop?: () => void;
  disabled?: boolean;
}

export default function SearchBar({ onSearch, onStop, disabled = false }: Props) {
  const [value, setValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasBeeThinkingModel, setHasBeeThinkingModel] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [thinkCode, setThinkCode] = useState("");
  const { t } = useTranslation();

  // 从全局状态获取深度思考状态
  const isDeepThinking = useCounterStore((state) => state.isDeepThinking);
  const setIsDeepThinking = useCounterStore((state) => state.setIsDeepThinking);

  // 获取模型列表并检查是否存在 bee-thinking 模型
  useEffect(() => {
    const checkBeeThinkingModel = async () => {
      try {
        const modalList = await modelService.getModelList();
        const hasModel = modalList.some((model) => model.name === "bee-thinking");
        setThinkCode(modalList.find((model) => model.name === "bee-thinking")?.code || "");
        console.log("hasModel", hasModel);
        setHasBeeThinkingModel(hasModel);
      } catch (error) {
        console.error("获取模型列表失败:", error);
        setHasBeeThinkingModel(false);
      }
    };

    checkBeeThinkingModel();
  }, []);

  // 自动调整文本区域高度
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // 重置高度以正确计算滚动高度
    textarea.style.height = "auto";

    // 计算所需高度
    const lineHeight = parseInt(getComputedStyle(textarea).lineHeight);
    const padding =
      parseInt(getComputedStyle(textarea).paddingTop) + parseInt(getComputedStyle(textarea).paddingBottom);

    const scrollHeight = textarea.scrollHeight - padding;
    const maxHeight = lineHeight * 6; // 最大6行高度

    // 设置高度
    textarea.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
  }, [value]);

  const handleSubmit = async () => {
    if (value.trim() && !disabled && !isSubmitting) {
      setIsSubmitting(true);
      const trimmedValue = value.trim();
      // 立刻清空输入框
      setValue("");
      try {
        // 获取 teamId
        const teamId = envStorage.getItem("teamId");
        let modelCode: string | undefined;

        // 如果选中了深度思考，使用深度思考模型代码
        if (isDeepThinking && thinkCode) {
          modelCode = thinkCode;
          console.log("使用深度思考模型:", modelCode);
        }
        // 如果 teamId 不为 1，默认使用 bee-model-v1
        else if (teamId && teamId !== "1") {
          modelCode = "bee-model-v1";
          console.log("使用默认模型 bee-model-v1");
        } else {
          console.log("不传递模型代码，使用系统默认模型");
        }
        // 否则不传递模型代码（使用默认模型）

        console.log("当前深度思考状态:", isDeepThinking, "最终使用的 modelCode:", modelCode);
        await onSearch(trimmedValue, modelCode);
      } catch (error) {
        console.error("Search failed:", error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Shift + Enter: 插入换行符
    if (e.key === "Enter" && e.shiftKey) {
      e.preventDefault();
      const cursorPosition = e.currentTarget.selectionStart;
      const newValue = value.substring(0, cursorPosition) + "\n" + value.substring(cursorPosition);

      setValue(newValue);

      // 保持光标位置
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.setSelectionRange(cursorPosition + 1, cursorPosition + 1);
        }
      }, 0);
    }
    // Enter: 发送消息（仅在未提交状态下允许）
    else if (e.key === "Enter" && !e.shiftKey && !isSubmitting) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="flex flex-col justify-center mb-[20px]">
      <div></div>
      <div className="gap-2 mx-auto search-input dark:bg-[#303030] rounded-[28px]">
        <Textarea
          ref={textareaRef}
          placeholder={t("chat.inputPlaceholder")}
          value={value}
          className="rounded-full border-none pl-0 pr-0 pt-[12px]"
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
        />

        <div className="flex justify-between items-center mt-2">
          <div className="flex items-center gap-2">
            {/* 深度思考按钮 */}
            {hasBeeThinkingModel && (
              <button
                type="button"
                tabIndex={-1}
                disabled={isSubmitting}
                onClick={() => {
                  if (isSubmitting) return;
                  setIsDeepThinking(!isDeepThinking);
                }}
                className={cn(
                  "deep-thinking-toggle",
                  isDeepThinking && "deep-thinking-toggle--active",
                  isSubmitting && "deep-thinking-toggle--disabled"
                )}>
                <ThinkingIcon />
                <span className="deep-thinking-toggle__label">{t("chat.Thinking") || "深度思考"}</span>
              </button>
            )}
            <div className="text-xs text-gray-500 pl-2">
              {/*<span className="hidden sm:inline">Shift+Enter 换行</span>*/}
              {/*<span className="mx-2 hidden sm:inline">|</span>*/}
              {/*<span>Enter 发送</span>*/}
            </div>
          </div>

          <div
            onClick={!isSubmitting ? handleSubmit : undefined}
            className={`rounded-full search_button mx-4 ${isSubmitting ? "cursor-not-allowed" : "cursor-pointer"}`}>
            {disabled ? (
              <img onClick={onStop} src={stop} className="w-[32px] h-[32px] cursor-pointer" alt="停止" />
            ) : !value.trim() ? (
              <img src={enterRead} className="w-[32px] h-[32px] cursor-not-allowed" alt="未输入" />
            ) : (
              <img src={enter} className="w-[32px] h-[32px]" alt="发送" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
